import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import api from 'utils/api';
import { CURRENCY_FALLBACKS } from 'utils/currency';

const CurrencyContext = createContext(null);

let activeCurrencyPromise = null;

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState({
    code: 'AED',
    symbol: 'Ð',
    name: 'UAE Dirham'
  });

  const fetchActiveCurrency = useCallback(async (force = false) => {
    if (activeCurrencyPromise && !force) {
      return activeCurrencyPromise;
    }
    activeCurrencyPromise = (async () => {
      try {
        const res = await api.get('/currency/list');
        if (res.data && res.data.status && Array.isArray(res.data.data)) {
          const active = res.data.data.find((c) => c.status === true) || res.data.data[0];
          if (active) {
            const rawSymbol = active.symbol || 'AED';
            const resolvedSymbol = CURRENCY_FALLBACKS[rawSymbol] || rawSymbol;
            const newCurrency = {
              code: rawSymbol,
              symbol: resolvedSymbol,
              name: active.name || 'UAE Dirham'
            };
            setCurrency(newCurrency);
            return newCurrency;
          }
        }
      } catch (err) {
        console.error('Error fetching active currency:', err);
        // Clear promise on failure to allow retry later
        activeCurrencyPromise = null;
      }
      return null;
    })();
    return activeCurrencyPromise;
  }, []);

  useEffect(() => {
    fetchActiveCurrency();
  }, [fetchActiveCurrency]);

  const value = useMemo(
    () => ({
      currency,
      refreshCurrency: () => fetchActiveCurrency(true)
    }),
    [currency, fetchActiveCurrency]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

CurrencyProvider.propTypes = {
  children: PropTypes.node
};

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
